import type * as React from 'react';
import { Check, Pencil, Trash2, X } from 'lucide-react';

import { AppSelect } from '../../../../components/AppSelect';
import { ClampedTextWithTooltip } from '../../../../components/ClampedTextWithTooltip';
import { TableSkeletonRows } from '../../../../components/TableSkeleton';
import { useHorizontalWheelScroll } from '../../../../hooks/useHorizontalWheelScroll';
import { parseGermanNumber } from '../../../../utils/parseGermanNumber';
import type { Material } from '../../types/material';
import {
  calcMaterialVk,
  formatMaterialPrice,
  formatMaterialVatDisplay,
  isOmdCatalogMaterial,
  MATERIAL_UNIT_OPTIONS,
  VAT_OPTIONS,
} from '../../utils/materialForm';

export type MaterialDesktopTableProps = {
  materials: Material[];
  loading: boolean;
  emptyMessage: string;
  editingId: string | null;
  editDraft: Partial<Material>;
  setEditDraft: React.Dispatch<React.SetStateAction<Partial<Material>>>;
  saving: boolean;
  onStartEdit: (m: Material) => void;
  onCancelEdit: () => void;
  onSaveEdit: (m: Material, draft: Partial<Material>) => void;
  onDelete: (m: Material) => void;
  onOpenOmdPanel: (m: Material) => void;
  omdEnabledSlugs: ReadonlySet<string>;
};

function formatMetalKg(value: number): string {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 6 }).format(value);
}

export function MaterialDesktopTable({
  materials,
  loading,
  emptyMessage,
  editingId,
  editDraft,
  setEditDraft,
  saving,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onOpenOmdPanel,
  omdEnabledSlugs,
}: MaterialDesktopTableProps) {
  // Die Tabelle kann breiter als der Viewport werden — Shift+Rad ersetzt das Ziehen der Leiste.
  const setTableScrollNode = useHorizontalWheelScroll<HTMLDivElement>();

  return (
    <div
      ref={setTableScrollNode}
      className="app-scrollbar overflow-x-auto md:min-h-0 md:flex-1 md:overflow-auto"
    >
      <table className="hidden md:table min-w-full text-sm border-collapse text-left">
        {/* Trennlinie als Innenschatten: bei border-collapse wandert ein `border-b` nicht mit der klebenden Zelle. */}
        <thead>
          <tr>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
              Bezeichnung
            </th>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
              Marke
            </th>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
              Lieferant
            </th>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
              G-Handels Nr.
            </th>
            <th scope="col" className="sticky top-0 z-10 max-w-[200px] bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
              Beschreibung
            </th>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
              Einheit
            </th>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-left text-xs font-semibold text-app-text-subtle">
              Metallanteil
            </th>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-right text-xs font-semibold text-app-text-subtle">
              Listenpreis
            </th>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-right text-xs font-semibold text-app-text-subtle">
              EK
            </th>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-right text-xs font-semibold text-app-text-subtle">
              Zuschlag %
            </th>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-right text-xs font-semibold text-app-text-subtle">
              VK
            </th>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-right text-xs font-semibold text-app-text-subtle">
              MwSt.
            </th>
            <th scope="col" className="sticky top-0 z-10 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-right text-xs font-semibold text-app-text-subtle">
              Min.
            </th>
            <th
              scope="col"
              className="sticky top-0 z-10 w-24 bg-app-surface-muted px-3 py-2.5 shadow-[inset_0_-1px_0_var(--color-pipedrive-border)] text-right text-xs font-semibold text-app-text-subtle"
              aria-label="Aktionen"
            />
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <TableSkeletonRows columns={14} label="Materialien werden geladen …" />
          ) : materials.length === 0 ? (
            <tr>
              <td colSpan={14} className="px-3 py-4 text-center text-xs text-app-text-subtle">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            materials.map((m) => {
                const isEditing = editingId === m.id;
                const draft = isEditing ? { ...m, ...editDraft } : m;
                const editVkPreview = isEditing
                  ? calcMaterialVk(
                      draft.net_price != null ? String(draft.net_price).replace('.', ',') : '',
                      String(draft.surcharge_percent ?? 0)
                    )
                  : null;

                return (
                  <tr
                    key={m.id}
                    className={`border-b border-app-border hover:bg-app-hover text-app-text ${m.kind === 'shared' ? 'bg-app-surface-muted/40' : ''} ${isOmdCatalogMaterial(m, omdEnabledSlugs) ? 'cursor-pointer' : ''}`}
                    onClick={
                      isOmdCatalogMaterial(m, omdEnabledSlugs) && !isEditing ? () => onOpenOmdPanel(m) : undefined
                    }
                  >
                    <td className="px-3 py-2">
                      {isEditing && m.kind !== 'shared' ? (
                        <input
                          value={draft.name}
                          onChange={(e) => setEditDraft((prev) => ({ ...prev, name: e.target.value }))}
                          className="w-full border border-app-border rounded px-2 py-1 text-xs text-app-text"
                        />
                      ) : (
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="truncate flex-1 min-w-0">{m.name}</span>
                          {m.kind === 'shared' && (
                            <span
                              className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-app-surface-muted text-app-text-muted"
                              title="Großhändler-Material (zentral verwaltet)"
                            >
                              Katalog
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isEditing && m.kind !== 'shared' ? (
                        <input
                          value={draft.brand || ''}
                          onChange={(e) =>
                            setEditDraft((prev) => ({ ...prev, brand: e.target.value }))
                          }
                          className="w-full border border-app-border rounded px-2 py-1 text-xs text-app-text"
                        />
                      ) : (
                        m.brand || '-'
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isEditing && m.kind !== 'shared' ? (
                        <input
                          value={draft.supplier || ''}
                          onChange={(e) =>
                            setEditDraft((prev) => ({ ...prev, supplier: e.target.value }))
                          }
                          className="w-full border border-app-border rounded px-2 py-1 text-xs text-app-text"
                        />
                      ) : (
                        <span className="truncate">{m.supplier || '-'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isEditing && m.kind !== 'shared' ? (
                        <input
                          value={draft.gtin || ''}
                          onChange={(e) => setEditDraft((prev) => ({ ...prev, gtin: e.target.value }))}
                          className="w-full border border-app-border rounded px-2 py-1 text-xs text-app-text"
                        />
                      ) : (
                        m.gtin || '-'
                      )}
                    </td>
                    <td className="px-3 py-2 max-w-[220px]">
                      {isEditing ? (
                        <textarea
                          value={draft.description ?? ''}
                          onChange={(e) =>
                            setEditDraft((prev) => ({
                              ...prev,
                              description: e.target.value || null,
                            }))
                          }
                          rows={2}
                          className="w-full border border-app-border rounded px-2 py-1 text-xs resize-y min-w-[160px] text-app-text"
                        />
                      ) : (
                        <ClampedTextWithTooltip text={m.description} className="text-xs text-app-text" />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <AppSelect
                          value={draft.unit || ''}
                          onChange={(v) => setEditDraft((prev) => ({ ...prev, unit: v || '' }))}
                          options={MATERIAL_UNIT_OPTIONS}
                          size="sm"
                        />
                      ) : (
                        m.unit || '-'
                      )}
                    </td>
                    <td className="px-3 py-2 min-w-[150px]">
                      {m.metal_kind && m.metal_kg_per_unit != null ? (
                        <div className="space-y-0.5 text-xs tabular-nums">
                          <div className="font-medium text-app-text">
                            {m.metal_kind === 'cu' ? 'Kupfer' : 'Aluminium'} ·{' '}
                            {formatMetalKg(m.metal_kg_per_unit)} kg/{m.unit || 'ME'}
                          </div>
                          <div className="text-app-text-subtle">
                            Basis {formatMaterialPrice(m.metal_basis_eur_per_100kg)} / 100 kg
                          </div>
                          <div className="text-app-text-subtle">
                            Zuschlag {formatMaterialPrice(m.metal_surcharge)} / {m.unit || 'ME'}
                          </div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {m.kind === 'shared' && m.list_price != null ? formatMaterialPrice(m.list_price) : '-'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isEditing ? (
                        <input
                          value={draft.net_price != null ? String(draft.net_price) : ''}
                          onChange={(e) =>
                            setEditDraft((prev) => ({
                              ...prev,
                              net_price: parseGermanNumber(e.target.value),
                            }))
                          }
                          className="w-24 border border-app-border rounded px-2 py-1 text-xs text-right text-app-text"
                        />
                      ) : (
                        formatMaterialPrice(m.net_price)
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isEditing ? (
                        <input
                          value={
                            draft.surcharge_percent != null && draft.surcharge_percent !== undefined
                              ? String(draft.surcharge_percent)
                              : ''
                          }
                          onChange={(e) => {
                            const v = e.target.value.trim();
                            setEditDraft((prev) => ({
                              ...prev,
                              surcharge_percent: v === '' ? 0 : parseGermanNumber(v),
                            }));
                          }}
                          className="w-16 border border-app-border rounded px-2 py-1 text-xs text-right text-app-text"
                        />
                      ) : typeof m.surcharge_percent === 'number' ? (
                        `${m.surcharge_percent} %`
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isEditing && editVkPreview != null
                        ? formatMaterialPrice(editVkPreview)
                        : formatMaterialPrice(m.gross_price)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isEditing ? (
                        <div className="flex justify-end">
                          <div className="w-[4.5rem]">
                            <AppSelect
                              value={String(
                                draft.vat_percent === 0 || draft.vat_percent === 7 || draft.vat_percent === 19
                                  ? draft.vat_percent
                                  : 19
                              )}
                              onChange={(v) =>
                                setEditDraft((prev) => ({
                                  ...prev,
                                  vat_percent: Number(v),
                                }))
                              }
                              options={VAT_OPTIONS}
                              size="sm"
                            />
                          </div>
                        </div>
                      ) : (
                        formatMaterialVatDisplay(m.vat_percent)
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={draft.minutes != null ? String(draft.minutes) : ''}
                          onChange={(e) => {
                            const v = e.target.value.trim();
                            setEditDraft((prev) => ({
                              ...prev,
                              minutes: v === '' ? null : Math.max(0, parseInt(v, 10) || 0),
                            }));
                          }}
                          className="w-16 border border-app-border rounded px-2 py-1 text-xs text-right text-app-text"
                          placeholder="-"
                        />
                      ) : (
                        (m.minutes != null ? `${m.minutes} Min` : '-')
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center p-1.5 rounded hover:bg-green-50 disabled:opacity-40"
                            title="Speichern"
                            aria-label="Speichern"
                            disabled={saving}
                            onClick={() => onSaveEdit(m, draft)}
                          >
                            <Check className="w-4 h-4 text-green-600" />
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center p-1.5 rounded hover:bg-app-hover"
                            title="Abbrechen"
                            aria-label="Abbrechen"
                            onClick={onCancelEdit}
                          >
                            <X className="w-4 h-4 text-app-text-subtle" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            className="p-1 rounded hover:bg-app-hover"
                            onClick={(e) => {
                              e.stopPropagation();
                              onStartEdit(m);
                            }}
                          >
                            <Pencil className="w-4 h-4 text-app-text-subtle" />
                          </button>
                          <button
                            type="button"
                            className="p-1 rounded hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            title={
                              m.kind === 'shared'
                                ? 'Großhändler-Materialien können nicht gelöscht werden.'
                                : 'Material löschen'
                            }
                            disabled={m.kind === 'shared' || saving}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (m.kind === 'shared') return;
                              onDelete(m);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
          )}
        </tbody>
      </table>
    </div>
  );
}

export default MaterialDesktopTable;
